// TrainingPlanViewer.jsx
"use client";

import React, { useEffect, useState } from "react";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthProvider";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { calculatePaces } from "../lib/paceCalculator";
import { generateTrainingPlan } from "../lib/planGenerator";

// Same valid options as in TrainingDaysSelector.
const validOptions = [
    {
        id: "option1",
        block1: ["Monday", "Tuesday", "Wednesday"],
        block2: ["Friday", "Saturday"],
        ambiguousDays: ["Friday", "Saturday"],
    },
    {
        id: "option2",
        block1: ["Tuesday", "Wednesday", "Thursday"],
        block2: ["Saturday", "Sunday"],
        ambiguousDays: ["Saturday", "Sunday"],
    },
    {
        id: "option3",
        block1: ["Wednesday", "Thursday", "Friday"],
        block2: ["Sunday", "Monday"],
        ambiguousDays: ["Sunday", "Monday"],
    },
];

const TrainingPlanViewer = () => {
    const { user } = useAuth();
    const [latestTest, setLatestTest] = useState(null);
    const [trainingPlanDoc, setTrainingPlanDoc] = useState(null);
    const [populatedPlan, setPopulatedPlan] = useState(null);
    const [error, setError] = useState("");

    // Modal state
    const [selectedWorkout, setSelectedWorkout] = useState(null);

    const handleOpenWorkoutModal = (workout) => setSelectedWorkout(workout);
    const handleCloseWorkoutModal = () => setSelectedWorkout(null);

    // Utility: Color-code based on the workout title.
    const getWorkoutColor = (title) => {
        switch (title) {
            case "Test Day":
                return "bg-[#ff5f00]";
            case "vVO2max Run":
                return "bg-[#ffb900]";
            case "Easy Run":
                return "bg-[#1eb778]";
            case "Threshold Run":
                return "bg-[#006bc1]";
            case "Long Run":
                return "bg-[#7956ff]";
            default:
                return "bg-gray-300";
        }
    };

    // Listener for the latest six-minute test result.
    useEffect(() => {
        if (!user) return;

        const testsRef = collection(db, "users", user.uid, "sixMinTests");
        const q = query(testsRef, orderBy("createdAt", "desc"), limit(1));

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                if (!snapshot.empty) {
                    const doc = snapshot.docs[0];
                    const data = doc.data();
                    setLatestTest(data);
                    setError(""); // Clear any previous errors
                } else {
                    setLatestTest(null);
                }
            },
            (err) => {
                console.error("Error listening for test updates:", err?.message || err);
                setLatestTest(null);

                // Provide specific error messages based on error type
                if (err?.code === "permission-denied") {
                    setError("You don't have permission to view tests. Please sign in again.");
                } else if (err?.code === "unavailable") {
                    setError("Unable to connect to the server. Please check your internet connection.");
                } else {
                    setError("Failed to load test data. Please refresh the page.");
                }
            }
        );

        return () => unsubscribe();
    }, [user]);

    // Listener for the latest training plan document.
    useEffect(() => {
        if (!user) return;

        const plansRef = collection(db, "users", user.uid, "trainingPlans", "5k", "plans");
        const q = query(plansRef, orderBy("createdAt", "desc"), limit(1));

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                if (!snapshot.empty) {
                    const doc = snapshot.docs[0];
                    const data = doc.data();
                    setTrainingPlanDoc(data);
                    setError(""); // Clear any previous errors
                } else {
                    setTrainingPlanDoc(null);
                }
            },
            (err) => {
                console.error("Error listening for training plan updates:", err?.message || err);
                setTrainingPlanDoc(null);

                // Provide specific error messages based on error type
                if (err?.code === "permission-denied") {
                    setError("You don't have permission to view the training plan. Please sign in again.");
                } else if (err?.code === "unavailable") {
                    setError("Unable to connect to the server. Please check your internet connection.");
                } else {
                    setError("Failed to load training plan. Please refresh the page.");
                }
            }
        );

        return () => unsubscribe();
    }, [user]);

    // When both latestTest and trainingPlanDoc are available, regenerate the dynamic pace values.
    useEffect(() => {
        if (!latestTest || !trainingPlanDoc) return;

        const option = validOptions.find((opt) => opt.id === trainingPlanDoc.optionId);
        if (!option) return;

        const computedPaces = calculatePaces(
            latestTest.minutes,
            latestTest.seconds,
            latestTest.unit
        );

        const planLevel = trainingPlanDoc.planLevel ?? 2;

        const dynamicPlan = generateTrainingPlan(
            computedPaces,
            option,
            trainingPlanDoc.ambiguousSelections,
            planLevel
        );

        setPopulatedPlan(dynamicPlan);
    }, [latestTest, trainingPlanDoc]);

    // Compute paces so we can reuse them later.
    const computedPaces =
        latestTest && typeof latestTest.minutes === "number"
            ? calculatePaces(latestTest.minutes, latestTest.seconds, latestTest.unit)
            : null;

    // Render calendar grid.
    const renderCalendarGrid = () => {
        if (!populatedPlan || !trainingPlanDoc) return null;

        return (
            <>
                {Object.keys(populatedPlan).map((weekKey) => {
                    const weekDayWorkouts = populatedPlan[weekKey];
                    const mapping = {};

                    weekDayWorkouts.forEach((dw) => {
                        mapping[dw.day] = dw.workout;
                    });

                    const rowCells = [
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                        "Sunday",
                    ].map((day) => {
                        if (mapping[day]) {
                            const workout = mapping[day];
                            return (
                                <td
                                    key={day}
                                    className="border border-gray-300 p-2 text-center align-top"
                                >
                                    <button
                                        onClick={() => handleOpenWorkoutModal(workout)}
                                        className={`w-full h-full block rounded-md p-2 text-white font-semibold ${getWorkoutColor(
                                            workout.Title
                                        )}`}
                                    >
                                        {workout.Title}
                                        <br />
                                        <span className="text-sm font-normal">
                                            {workout["Distance/time"]}
                                        </span>
                                    </button>
                                </td>
                            );
                        }

                        return (
                            <td
                                key={day}
                                className="border border-gray-300 p-2 text-center text-gray-500"
                            >
                                Rest
                            </td>
                        );
                    });

                    return (
                        <tr key={weekKey} className="border border-gray-300">
                            <td className="border border-gray-300 p-2 font-semibold text-center">
                                {weekKey}
                            </td>
                            {rowCells}
                        </tr>
                    );
                })}
            </>
        );
    };

    if (!user) return <p className="text-center p-4">Please log in to view your training plan.</p>;
    if (error) return <p className="text-center p-4 text-red-500">{error}</p>;
    if (!latestTest) return <p className="text-center p-4">No 6-minute test found. Please complete a 6-minute test first.</p>;
    if (!trainingPlanDoc) return <p className="text-center p-4">No training plan found. Please create and save your training plan.</p>;

    // Pace display inside modal
    let paceDisplay = "";
    if (selectedWorkout && latestTest) {
        if (selectedWorkout.Title === "Easy Run" && computedPaces) {
            paceDisplay = `${selectedWorkout.Pace} ${computedPaces.easyRun.low} - ${computedPaces.easyRun.high} ${latestTest.unit}`;
        } else if (selectedWorkout.Pace) {
            let paceTemplate = selectedWorkout.Pace ?? "";
            if (computedPaces) {
                paceTemplate = paceTemplate
                    .replace("{Threshold Pace}", computedPaces.threshold)
                    .replace(
                        "{Easy Run Pace}",
                        `${computedPaces.easyRun.low} - ${computedPaces.easyRun.high}`
                    );
            }
            paceTemplate = paceTemplate.replace("{test.unit}", latestTest.unit);
            paceDisplay = paceTemplate;
        }
    }

    // Split description into sentences; URLs become links.
    const renderDescriptionParagraphs = (description) => {
        const sentences = description.split(/(?<=[.!?])\s+/);
        const urlRegex = /(https?:\/\/[^\s]+)/g;

        return (
            <div>
                {sentences.map((sentence, index) => {
                    const parts = sentence.split(urlRegex);
                    return (
                        <p key={index}>
                            {parts.map((part, i) => {
                                if (part.match(urlRegex)) {
                                    return (
                                        <a
                                            key={i}
                                            href={part}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-500 underline"
                                        >
                                            {part}
                                        </a>
                                    );
                                }
                                return part;
                            })}
                        </p>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="p-2">
            <h2 className="text-2xl font-bold mb-4">Your Training Plan</h2>
            <p className="mb-4">Click on each day to see the instructions for each workout.</p>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] border-collapse">
                    <thead>
                        <tr>
                            <th className="border border-gray-300 p-2">Week</th>
                            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                                <th key={day} className="border border-gray-300 p-2">
                                    {day}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>{renderCalendarGrid()}</tbody>
                </table>
            </div>

            {selectedWorkout && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    onClick={handleCloseWorkoutModal}
                >
                    <div
                        className="bg-white rounded-md p-4 max-w-md w-full mx-2 relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={handleCloseWorkoutModal}
                            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors"
                            aria-label="Close modal"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        <h3 className="text-xl font-bold mb-2">{selectedWorkout.Title}</h3>

                        <p className="mb-2">
                            <span className="font-semibold">Distance/Time:</span>{" "}
                            {selectedWorkout["Distance/time"]}
                        </p>

                        {selectedWorkout.Pace && (
                            <div className="mb-2">
                                <p className="font-semibold">Pace</p>
                                <p>{paceDisplay}</p>
                            </div>
                        )}

                        <div className="mb-4">
                            <p className="font-semibold">Description</p>
                            {renderDescriptionParagraphs(selectedWorkout.Description)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingPlanViewer;
